import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Select, 
  Button, 
  Space, 
  Tag, 
  message,
  Typography,
  Avatar
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import masterService from '../../services/masterService';
import { useUser } from '../../context/UserContext';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const OnBoardingList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [onboardingList, setOnboardingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  
  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    status: null
  });
  
  // Plan selection state
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Table columns configuration
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 80,
      render: (text, record, index) => index + 1
    },
    {
      title: 'Order',
      dataIndex: 'mob_order',
      key: 'mob_order',
      width: 100,
      sorter: (a, b) => a.mob_order - b.mob_order
    },
    {
      title: 'ID',
      dataIndex: 'mob_id',
      key: 'mob_id',
      width: 100,
      sorter: (a, b) => a.mob_id - b.mob_id
    },
    {
      title: 'Name',
      dataIndex: 'mob_name',
      key: 'mob_name',
      sorter: (a, b) => a.mob_name.localeCompare(b.mob_name)
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (text) => (
        <Tag color={text === 'Y' ? 'green' : 'red'}>
          {text === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'Y' },
        { text: 'Inactive', value: 'N' }
      ],
      onFilter: (value, record) => record.is_active === value
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/onboarding/onboarding-form/${record.mob_id}`)}
            size="small"
          >
            Edit
          </Button>
        </Space>
      )
    }
  ];

  // Fetch onboarding data
  const getData = async () => {
    try {
      setLoading(true);
      console.log('Fetching onboarding data with filters:', filters);
      
      const response = await masterService.getAllOnBoarding(filters);
      console.log('API Response:', response);
      
      if (response.data && response.data.status) {
        setOnboardingList(response.data.data || []);
        // Set the first item's plan_id as default
        if (response.data.data && response.data.data.length > 0) {
          setSelectedPlanId(response.data.data[0].plan_id);
        }
      } else {
        console.log('API returned no data or status false:', response.data);
        setOnboardingList([]);
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
      message.error('Failed to fetch onboarding data');
      setOnboardingList([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription plans
  const getSubscriptionPlans = async () => {
    try {
      const response = await masterService.getSubscriptionPlans();
      console.log('Subscription Plans Response:', response);
      
      if (response.data && response.data.status) {
        setSubscriptionPlans(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      message.error('Failed to fetch subscription plans');
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
  };

  // Handle plan selection change
  const handlePlanChange = (value) => {
    setSelectedPlanId(value);
  };

  // Save order and plan
  const saveOrderAndPlan = async () => {
    try {
      setSaving(true);
      console.log('Saving order and plan:', {
        onboarding_arr: onboardingList,
        plan_id: selectedPlanId
      });
      
      const response = await masterService.saveOnBoardingOrderAndPlan({
        onboarding_arr: onboardingList,
        plan_id: selectedPlanId
      });
      
      console.log('Save Response:', response);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Order and plan saved successfully!');
      } else {
        message.error(response.data.message || 'Failed to save order and plan');
      }
    } catch (error) {
      console.error('Error saving order and plan:', error);
      message.error('Failed to save order and plan');
    } finally {
      setSaving(false);
    }
  };

  // Handle add new onboarding
  const handleAddNew = () => {
    navigate('/onboarding/onboarding-form/new');
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    getData();
  }, [filters.search, filters.status]);

  // Load subscription plans on mount
  useEffect(() => {
    getSubscriptionPlans();
  }, []);

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">OnBoarding Management</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="OnBoarding Management"
      desc="Manage onboarding items and their order"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        {/* Table Operations */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              style={{ marginRight: '15px' }}
            >
              Add
            </Button>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Select
              placeholder="Status"
              style={{ width: 150 }}
              value={filters.status}
              onChange={handleStatusChange}
              allowClear
            >
              <Option value={null}>All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            
            <Search
              placeholder="Search..."
              style={{ width: 300 }}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onSearch={handleSearch}
              allowClear
            />
          </div>
        </div>

        {/* Plan Selection and Save */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Text strong>Select Plan:</Text>
            <Select
              style={{ width: 300 }}
              value={selectedPlanId}
              onChange={handlePlanChange}
              placeholder="Please select Plans"
              showSearch
              optionFilterProp="children"
            >
              {subscriptionPlans.map(plan => (
                <Option key={plan.plan_id} value={plan.plan_id}>
                  {plan.plan_name}
                </Option>
              ))}
            </Select>
          </div>
          
          <Button 
            type="primary" 
            onClick={saveOrderAndPlan}
            loading={saving}
          >
            Save Order And Plan
          </Button>
        </div>

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={onboardingList}
          rowKey="mob_id"
          loading={loading}
          scroll={{ x: 600 }}
          size="middle"
          pagination={false}
        />
      </Card>
    </PageLayout>
  );
};

export default OnBoardingList;